import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Alert,
  Button,
  Typography,
  Snackbar,
  Fade,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { AINewsSkeleton } from '../components/ai-news';
import MarketPredictionHero from '../components/ai-news/MarketPredictionHero';
import IndicatorImpactGrid from '../components/ai-news/IndicatorImpactGrid';
import RecentArticlesList from '../components/ai-news/RecentArticlesList';
import './AINews.css';

const AINews = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [researching, setResearching] = useState(false);
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [shockData, setShockData] = useState(null);
  const [indicatorImpacts, setIndicatorImpacts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [articlesLimit, setArticlesLimit] = useState(20);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [newArticlesCount, setNewArticlesCount] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, healthRes, impactsRes, forecastRes, shockRes] = await Promise.all([
        api.get('/ai-news/summary/latest'),
        api.get('/ai-news/health?days=7'),
        api.get('/ai-news/indicators/impact?days=7'),
        api.get('/ai-news/forecast/weekly'),
        api.get('/shock/summary')
      ]);

      // prefer article-level weekly counts from health endpoint; fallback to indicator impacts sum
      const impacts = impactsRes.data || [];
      const impactsWeeklyTotal = impacts.reduce((s, it) => s + (it.article_count || 0), 0);
      const healthWeeklyTotal = healthRes?.data?.total_articles;
      const weeklyTotal = (typeof healthWeeklyTotal === 'number') ? healthWeeklyTotal : impactsWeeklyTotal;

      const newSummary = {
        ...summaryRes.data,
        total_articles_week: weeklyTotal,
        weekly_positive_news: healthRes?.data?.positive_news,
        weekly_neutral_news: healthRes?.data?.neutral_news,
        weekly_negative_news: healthRes?.data?.negative_news
      };

      setSummary((previousSummary) => {
        const hasNewData = !previousSummary
          || newSummary.total_articles !== previousSummary.total_articles
          || newSummary.indicators_analyzed !== previousSummary.indicators_analyzed;

        if (hasNewData && previousSummary) {
          setSnackbar({
            open: true,
            message: t('aiNews.dataRefreshed') + ` (${newSummary.total_articles} articles, ${newSummary.indicators_analyzed} indicators)`,
            severity: 'success'
          });
        }

        return newSummary;
      });
      setIndicatorImpacts(impactsRes.data);
      setForecast(forecastRes.data);
      setShockData({ summary: shockRes.data, health: healthRes.data });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching AI news data:', err);
      setError(err.response?.data?.detail || 'Failed to load AI news data');
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, [t]);

  const triggerDailyResearch = async () => {
    setResearching(true);
    setSnackbar({
      open: true,
      message: t('aiNews.researchStarted'),
      severity: 'info'
    });
    
    try {
      await api.post('/ai-news/research/daily');
      
      setSnackbar({
        open: true,
        message: t('aiNews.researchInProgress') + ' (This may take 2-3 minutes)',
        severity: 'success'
      });
      
      // Poll for updates every 30 seconds for up to 3 minutes
      let pollCount = 0;
      const maxPolls = 6; // 6 * 30 seconds = 3 minutes
      
      const pollInterval = setInterval(async () => {
        pollCount++;
        console.log(`Polling for updates... (${pollCount}/${maxPolls})`);
        
        try {
          await fetchDataSilently();
          
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setResearching(false);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 30000); // Poll every 30 seconds
      
      // Stop researching state after 3 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setResearching(false);
      }, 180000); // 3 minutes
      
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('aiNews.researchFailed') + ': ' + (err.response?.data?.detail || err.message),
        severity: 'error'
      });
      setResearching(false);
    }
  };
  
  const fetchDataSilently = useCallback(async () => {
    try {
      const [summaryRes, healthRes, impactsRes, forecastRes, shockRes] = await Promise.all([
        api.get('/ai-news/summary/latest'),
        api.get('/ai-news/health?days=7'),
        api.get('/ai-news/indicators/impact?days=7'),
        api.get('/ai-news/forecast/weekly'),
        api.get('/shock/summary')
      ]);

      const newSummary = { ...summaryRes.data };
      const impacts = impactsRes.data || [];
      const impactsWeeklyTotal = impacts.reduce((s, it) => s + (it.article_count || 0), 0);
      const healthWeeklyTotal = healthRes?.data?.total_articles;
      newSummary.total_articles_week = (typeof healthWeeklyTotal === 'number') ? healthWeeklyTotal : impactsWeeklyTotal;
      newSummary.weekly_positive_news = healthRes?.data?.positive_news;
      newSummary.weekly_neutral_news = healthRes?.data?.neutral_news;
      newSummary.weekly_negative_news = healthRes?.data?.negative_news;

      setSummary((previousSummary) => {
        const hasNewData = !previousSummary
          || newSummary.total_articles !== previousSummary.total_articles
          || newSummary.indicators_analyzed !== previousSummary.indicators_analyzed;

        if (hasNewData && previousSummary) {
          setSnackbar({
            open: true,
            message: t('aiNews.dataRefreshed') + ` (${newSummary.total_articles} articles, ${newSummary.indicators_analyzed} indicators)`,
            severity: 'success'
          });
        }

        return newSummary;
      });
      setIndicatorImpacts(impactsRes.data);
      setForecast(forecastRes.data);
      setShockData({ summary: shockRes.data, health: healthRes.data });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching AI news data:', err);
      // Don't show error snackbar during polling to avoid spam
    } finally {
      setIsPolling(false);
    }
  }, [t]);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await api.get('/ai-news/providers');
      setProviders(res.data || []);
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: articlesLimit.toString() });
      if (selectedProvider && selectedProvider !== 'all') {
        params.append('provider', selectedProvider);
      }
      const res = await api.get(`/ai-news/articles/recent?${params.toString()}`);
      const newArticles = res.data || [];

      setArticles((previousArticles) => {
        if (previousArticles.length > 0 && newArticles.length > 0) {
          const previousLatestId = previousArticles[0]?.id;
          const newLatestId = newArticles[0]?.id;
          if (previousLatestId && newLatestId && newLatestId !== previousLatestId) {
            const newCount = newArticles.findIndex((article) => article.id === previousLatestId);
            if (newCount > 0) {
              setNewArticlesCount(newCount);
              setSnackbar({
                open: true,
                message: `${newCount} ${t('aiNews.newArticles')}`,
                severity: 'info'
              });
            }
          }
        }

        return newArticles;
      });
    } catch (err) {
      console.error('Error fetching articles:', err);
    }
  }, [articlesLimit, selectedProvider, t]);

  const fetchArticlesSilently = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: articlesLimit.toString() });
      if (selectedProvider && selectedProvider !== 'all') {
        params.append('provider', selectedProvider);
      }
      const res = await api.get(`/ai-news/articles/recent?${params.toString()}`);
      const newArticles = res.data || [];

      setArticles((previousArticles) => {
        if (previousArticles.length > 0 && newArticles.length > 0) {
          const previousLatestId = previousArticles[0]?.id;
          const newLatestId = newArticles[0]?.id;
          if (previousLatestId && newLatestId && newLatestId !== previousLatestId) {
            const newCount = newArticles.findIndex((article) => article.id === previousLatestId);
            if (newCount > 0) {
              setNewArticlesCount(newCount);
              setSnackbar({
                open: true,
                message: `${newCount} ${t('aiNews.newArticles')}`,
                severity: 'info'
              });
            }
          }
        }

        return newArticles;
      });
    } catch (err) {
      console.error('Error fetching articles silently:', err);
    }
  }, [articlesLimit, selectedProvider, t]);

  useEffect(() => {
    fetchData();
    fetchProviders();
    fetchArticles();

    const pollInterval = setInterval(() => {
      setIsPolling(true);
      fetchDataSilently();
      fetchArticlesSilently();
    }, 30000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [fetchArticles, fetchArticlesSilently, fetchData, fetchDataSilently, fetchProviders]);

  useEffect(() => {
    if (!loading) {
      fetchArticles();
    }
  }, [fetchArticles, loading]);

  // Loading State
  if (loading) {
    return <AINewsSkeleton />;
  }

  // Error State
  if (error && !summary) {
    return (
      <Fade in timeout={600}>
        <Box>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={triggerDailyResearch}
            disabled={researching}
            sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600 }}
          >
            {researching ? 'Starting Research...' : 'Run First Research'}
          </Button>
        </Box>
      </Fade>
    );
  }

  // Main Content — dashboard layout
  return (
    <Box>
      {/* ── Compact Header ── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2.5 }}
      >
        {/* Left: title + status */}
        <Box display="flex" alignItems="center" gap={1.5}>
          <AIIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            AI News Radar
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.72rem' }}>
              {t('aiNews.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          {isPolling && (
            <Chip
              label={t('aiNews.updating')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.6 } }
              }}
            />
          )}
        </Box>

        {/* Right: actions */}
        <Box display="flex" gap={1} alignItems="center">
          {newArticlesCount > 0 && (
            <Chip
              label={`${newArticlesCount} ${t('aiNews.newArticles')}`}
              size="small"
              color="success"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
              onDelete={() => setNewArticlesCount(0)}
            />
          )}
          <Button
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
            onClick={fetchData}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }}
          >
            {t('aiNews.refresh')}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AssessmentIcon sx={{ fontSize: 18 }} />}
            onClick={triggerDailyResearch}
            disabled={researching}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', borderRadius: 1.5 }}
          >
            {researching ? t('aiNews.researching') : t('aiNews.runResearch')}
          </Button>
        </Box>
      </Box>

      {summary && (
        <Fade in timeout={600}>
          <Box>
            {/* ── Hero: Prediction + Stats ── */}
            <MarketPredictionHero
              weeklyForecast={forecast}
              shockData={shockData}
              summary={summary}
            />

            {/* ── Indicator Impact Chart (full width) ── */}
            <Box sx={{ mt: 2.5 }}>
              <IndicatorImpactGrid indicatorImpacts={indicatorImpacts} />
            </Box>

            {/* ── Recent Articles ── */}
            <Box sx={{ mt: 2.5 }}>
              <RecentArticlesList
                articles={articles}
                limit={articlesLimit}
                onLimitChange={setArticlesLimit}
                providers={providers}
                selectedProvider={selectedProvider}
                onProviderChange={setSelectedProvider}
              />
            </Box>
          </Box>
        </Fade>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slots={{ transition: Fade }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}` }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AINews;
